import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useUserContext } from './user';
import { useThemeContext } from 'contexts';
import endpoint, { ErrorResponse } from 'utils/endpoint';
import { trackEvent } from 'utils/google-tag-manager';
import { useRouter } from 'next/router';
import { AREA_LISTINGS_ROUTE, getRouteNameFromPath, ADDRESS_ROUTE } from 'components/dynamic-page/route-matchers';
import {
  GTM_CLICK_ADDRESS_PAGE_FAVOURITE_ICON,
  GTM_CLICK_FAV_ON_AREA_PAGE,
  GTM_CLICK_FAV_ON_MAP,
  GTM_CLICK_FAV_ON_MODAL,
} from '../constants/events';
import Listing from 'data/listing';

import type { SavedListingsParams, Meta } from 'utils/types';
import { withTryAsync } from 'utils/withTry';

const API_V4_BASE_URL = '/services/api/v4/saved-listings';

export type SavedListingItem = {
  id: string;
  type: 'saved-listings';
  addressSlug: string;
  listing_id?: string;
};

// --- New Nested Response Types --- START ---
// Type for the attributes of a nested saved listing
type NestedSavedListingAttributes = {
  id: number;
  userId: number;
  source: string;
  isSaved: boolean;
  createdAt: string;
  updatedOn: string;
  addressSlug: string | null; // Can be null
  listingId: number | null; // Can be null
  listing: Record<string, any> | null; // Embedded listing attributes
};

// Type for a single item in the new nested response data array
export type NestedSavedListingDataItem = {
  id: string;
  type: 'nested-saved-listings';
  attributes: NestedSavedListingAttributes;
};

// Type for the overall nested response
export type NestedSavedListingsResponse = {
  data: NestedSavedListingDataItem[];
  meta: Meta;
};
// --- New Nested Response Types --- END ---

// Enhanced type for full listing data (remains the same, populated differently)
export type SavedListingFull = {
  id: string;
  type: 'saved-listings-full';
  addressSlug: string | null;
  listingId: number;
  listing?: Listing;
  isOriginalListing: boolean;
  hasActiveListing: boolean;
};

// List item version without the full listing data
export type SavedListingListItem = {
  id: string;
  type: 'saved-listings-list';
  addressSlug: string | null;
  listingId: number;
  isOriginalListing: boolean;
  hasActiveListing: boolean;
};

export type AddSavedListingProps = {
  listingId: number;
  addressSlug?: string;
  isModal?: boolean;
};

export interface ISavedListingContext {
  savedListings: SavedListingListItem[];
  addSavedListing: (props: AddSavedListingProps) => void;
  removeSavedListing: (addressSlug: string) => void;
  isSavedListing: (addressSlug?: string, listingId?: number) => boolean;
  getSavedListings: (params: SavedListingsParams) => Promise<{ savedListings: SavedListingFull[]; meta: Meta }>;
}

export const SavedListingContext = createContext<ISavedListingContext | Record<string, unknown>>({});

export function useSavedListingContext <T extends ISavedListingContext | Record<string, unknown> = ISavedListingContext>() {
  return useContext(SavedListingContext) as T;
}

interface Props {
  children: ReactNode;
}

export default function SavedListingContextProvider({ children }: Props) {
  const { isAuthenticated } = useUserContext();
  const { themeName } = useThemeContext();
  const [savedListings, setSavedListings] = useState<SavedListingListItem[]>([]);
  const { asPath } = useRouter();

  // Create a map of addressSlug -> true for quick lookups
  const savedListingSlugMap = useMemo(() => {
    // Filter out items with null addressSlug before creating the map
    return new Map(savedListings.filter(item => item.addressSlug).map(item => [item.addressSlug, true]));
  }, [savedListings]);

  useEffect(() => {
    if (isAuthenticated) {
      syncSavedListing();
    } else {
      !!savedListings.length && setSavedListings([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const syncSavedListing = async () => {
    const [response, error] = await withTryAsync<NestedSavedListingsResponse, ErrorResponse>(endpoint)(API_V4_BASE_URL, 'GET', {
      // Fetch saved listing records with both available and unavailable listings
      filter: {
        source: themeName,
      },
    });

    if (error) {
      console.error('Failed to sync saved listings:', error);
    } else if (response?.data) {
      // Convert nested API response to SavedListingListItem objects
      const savedListingListItems = response.data.map(item => {
        const attrs = item.attributes;
        const hasActiveListing = attrs.listing && attrs.listing.isAvailable;
        const isOriginalListing = !!(attrs.listing && attrs.listingId === attrs.listing.id);
        return {
          id: item.id,
          type: 'saved-listings-list' as const,
          addressSlug: attrs.addressSlug,
          // Setting new listing ID here, so we can delete it by the saved listing ID
          // Looks like this wasn't implemented and removeSavedListing() still requires slug
          listingId: attrs.listing?.id || attrs.listingId,
          isOriginalListing: isOriginalListing,
          hasActiveListing: hasActiveListing,
        };
      });
      setSavedListings(savedListingListItems);
    } else {
      console.warn('syncSavedListing received successful response, but no data found.');
    }
  };

  const addSavedListing: ISavedListingContext['addSavedListing'] = async (props: AddSavedListingProps) => {
    const { listingId, addressSlug, isModal } = props;
    const routeName = getRouteNameFromPath(asPath);
    const isOnAreaPage = routeName === AREA_LISTINGS_ROUTE;
    const isOnAddressPage = routeName === ADDRESS_ROUTE;
    const isOnMapPage = asPath === '/search';

    if (isModal) {
      trackEvent(GTM_CLICK_FAV_ON_MODAL);
    } else if (isOnAreaPage) {
      trackEvent(GTM_CLICK_FAV_ON_AREA_PAGE);
    } else if (isOnAddressPage) {
      trackEvent(GTM_CLICK_ADDRESS_PAGE_FAVOURITE_ICON);
    } else if (isOnMapPage) {
      trackEvent(GTM_CLICK_FAV_ON_MAP);
    }

    const [response, error] = await withTryAsync<SavedListingItem, ErrorResponse>(endpoint)(API_V4_BASE_URL, 'POST', {
      listingId,
      addressSlug,
      source: themeName,
    });

    if (error) {
      console.error('Failed to add saved listing:', error);
    } else if (response) {
      // Convert API response to SavedListingListItem
      const newSavedListing: SavedListingListItem = {
        id: response.id,
        type: 'saved-listings-list',
        addressSlug: response.addressSlug,
        listingId: listingId,
        isOriginalListing: true,
        hasActiveListing: true,
      };

      // Add the new saved listing item to the state
      setSavedListings(prev => [...prev, newSavedListing]);
      // Refresh the saved listings after successful save
      syncSavedListing();
    }
  };

  const removeSavedListing: ISavedListingContext['removeSavedListing'] = async addressSlug => {
    const [, error] = await withTryAsync<{ data: Record<string, unknown> }, ErrorResponse>(endpoint)(API_V4_BASE_URL, 'DELETE', {
      addressSlug,
      source: themeName,
    });

    if (error) {
      console.error('Failed to remove saved listing:', error);
    } else {
      // Remove the saved listing with matching addressSlug
      setSavedListings(prev => prev.filter(item => item.addressSlug !== addressSlug));
    }
  };

  const getSavedListings = async (params: SavedListingsParams) => {
    const paramsWithSource = {
      ...params,
      filter: {
        ...params.filter,
        source: themeName,
      },
    };

    const [response, error] = await withTryAsync<NestedSavedListingsResponse, ErrorResponse>(endpoint)(API_V4_BASE_URL, 'GET', paramsWithSource);

    if (error) {
      console.error('Failed to get saved listings:', error);
      return { savedListings: [], meta: error.meta || {}};
    } else if (response?.data) {
      const { data, meta } = response;
      const enhancedSavedListings: SavedListingFull[] = [];

      data.forEach(nestedSavedListing => {
        const { attributes } = nestedSavedListing;
        let listing: Listing | undefined;
        let hasActiveListing = false;
        let isOriginal = true;

        if (attributes.listing) {
          try {
            listing = Listing.fromClientAppListing({
              type: 'listings',
              attributes: attributes.listing,
              // Pass id separately if constructor expects it
              id: attributes.listing.id?.toString() || '',
            });

            hasActiveListing = listing.isAvailable;
            isOriginal = attributes.listingId === listing.id;
          } catch (e) {
            console.error(`Error processing listing object for saved listing ID ${nestedSavedListing.id}:`, e);
            listing = undefined;
          }
        }

        const enhancedItem: SavedListingFull = {
          id: nestedSavedListing.id,
          type: 'saved-listings-full',
          addressSlug: attributes.addressSlug,
          listingId: attributes.listingId,
          listing: listing,
          hasActiveListing: hasActiveListing,
          isOriginalListing: isOriginal,
        };

        enhancedSavedListings.push(enhancedItem);
      });

      return { savedListings: enhancedSavedListings, meta };
    } else {
      console.warn('Received successful response for getSavedListings, but no data found.');
      return { savedListings: [], meta: response?.meta || {}};
    }
  };

  const isSavedListing = (addressSlug?: string, listingId?: number): boolean => {
    if (!addressSlug) {
      // If no addressSlug, fallback to check if listingId is in the savedListings array
      return savedListings.some(item => item.listingId === listingId);
    }
    return savedListingSlugMap.has(addressSlug);
  };

  return (
    <SavedListingContext.Provider
      value={{
        savedListings,
        addSavedListing,
        removeSavedListing,
        getSavedListings,
        isSavedListing,
      }}
    >
      {children}
    </SavedListingContext.Provider>
  );
}
